import React, { useState, useEffect } from 'react';
import { X, User, Users, Upload } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';

const ROLES = ['Super Admin', 'Admin', 'Supervisor', 'Staff'];
const STATUSES_KEYS = ['active', 'inactive'];

const UserManagementModal = ({ isOpen, onClose, user, projects, allUsers, onSave }) => {
    const { language } = useLanguage();
    const t = getTranslation(language);

    const getRoleDescriptions = () => ({
        'Super Admin': {
            title: 'Super Admin',
            access: t('userMgmt.superAdminDesc'),
            features: t('userMgmt.superAdminFeatures').split(','),
        },
        'Admin': {
            title: 'Admin',
            access: t('userMgmt.adminDesc'),
            features: t('userMgmt.adminFeatures').split(','),
        },
        'Supervisor': {
            title: 'Supervisor',
            access: t('userMgmt.supervisorDesc'),
            features: t('userMgmt.supervisorFeatures').split(','),
        },
        'Staff': {
            title: 'Staff',
            access: t('userMgmt.staffDesc'),
            features: t('userMgmt.staffFeatures').split(','),
        }
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        status: 'Aktif',
        isSuperAdmin: false,
        hideFinancials: false,
        access: [],
        avatar: ''
    });
    const [uploadedImage, setUploadedImage] = useState('');

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                password: '',
                status: user.status || 'Aktif',
                isSuperAdmin: user.isSuperAdmin || false,
                hideFinancials: user.hideFinancials || false,
                access: user.access || [],
                avatar: user.avatar || ''
            });
            setUploadedImage(user.avatar || '');
        } else {
            setFormData({
                name: '',
                email: '',
                password: '',
                status: 'Aktif',
                isSuperAdmin: false,
                hideFinancials: false,
                access: [],
                avatar: ''
            });
            setUploadedImage('');
        }
    }, [user, isOpen]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert(t('userMgmt.imageTooLarge'));
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedImage(reader.result);
                setFormData({ ...formData, avatar: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setUploadedImage('');
        setFormData({ ...formData, avatar: '' });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const toggleProjectAccess = (projectId) => {
        const hasAccess = formData.access.find(a => a.projectId === projectId);
        if (hasAccess) {
            setFormData({
                ...formData,
                access: formData.access.filter(a => a.projectId !== projectId)
            });
        } else {
            setFormData({
                ...formData,
                access: [...formData.access, { projectId, role: 'Admin' }]
            });
        }
    };

    const updateProjectRole = (projectId, role) => {
        setFormData({
            ...formData,
            access: formData.access.map(a =>
                a.projectId === projectId ? { ...a, role } : a
            )
        });
    };

    if (!isOpen) return null;

    const ROLE_DESCRIPTIONS = getRoleDescriptions();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-900">
                        {user ? t('userMgmt.editUserDetails') : t('userMgmt.createNewUser')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                    >
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    <div className="space-y-6">
                        {/* Avatar Section */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full bg-[#10056E]/10 text-[#10056E] flex items-center justify-center text-2xl font-bold overflow-hidden">
                                    {uploadedImage ? (
                                        <img src={uploadedImage} alt="Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        formData.name ? formData.name.charAt(0).toUpperCase() : <User size={32} />
                                    )}
                                </div>
                                {uploadedImage && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute -top-1 -right-1 w-7 h-7 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 transition-colors shadow-lg"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            
                            <div className="flex gap-2">
                                <label className="px-4 py-2 bg-[#10056E] text-white rounded-lg text-sm font-semibold hover:bg-[#0d0459] cursor-pointer transition-all flex items-center gap-2">
                                    <Upload size={16} />
                                    {t('userMgmt.uploadPhoto')}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-slate-500 text-center">
                                {t('userMgmt.imageFormatMax')}
                            </p>
                        </div>

                        {/* Basic Info */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                {t('userMgmt.fullNameLabel')} *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all"
                                placeholder={t('auth.fullName')}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                {t('userMgmt.emailAddressLabel')} *
                            </label>
                            <input
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all"
                                placeholder={t('auth.emailPlaceholder')}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                {t('userMgmt.passwordLabel')} {!user && '*'}
                            </label>
                            {user && (
                                <p className="text-xs text-slate-500 mb-2">
                                    {t('userMgmt.keepExistingPassword')}
                                </p>
                            )}
                            <input
                                type="password"
                                required={!user}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all"
                                placeholder="••••••••"
                            />
                        </div>

                        {/* Access Level */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                {t('userMgmt.accessLevel')}
                            </label>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, isSuperAdmin: false })}
                                    className={`px-4 py-4 rounded-xl font-semibold transition-all text-left ${
                                        !formData.isSuperAdmin
                                            ? 'bg-[#10056E] text-white shadow-lg shadow-[#10056E]/30'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    <div className="font-bold mb-1">{t('userMgmt.projectLevel')}</div>
                                    <div className={`text-xs ${!formData.isSuperAdmin ? 'text-white/70' : 'text-slate-500'}`}>
                                        {t('userMgmt.selectProjectRole')}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, isSuperAdmin: true, access: [] })}
                                    className={`px-4 py-4 rounded-xl font-semibold transition-all text-left ${
                                        formData.isSuperAdmin
                                            ? 'bg-[#10056E] text-white shadow-lg shadow-[#10056E]/30'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    <div className="font-bold mb-1">Super Admin</div>
                                    <div className={`text-xs ${formData.isSuperAdmin ? 'text-white/70' : 'text-slate-500'}`}>
                                        {t('userMgmt.fullAccessAllSystems')}
                                    </div>
                                </button>
                            </div>
                            {formData.isSuperAdmin && (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                    <div className="text-xs font-semibold text-amber-800 mb-2">
                                        {t('userMgmt.superAdminFullAccess')}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {ROLE_DESCRIPTIONS['Super Admin'].features.map((feature, idx) => (
                                            <span 
                                                key={idx}
                                                className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium"
                                            >
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Project Access - Only show if not Super Admin */}
                        {!formData.isSuperAdmin && (
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                    {t('userMgmt.selectProjectRoleLabel')}
                                </label>
                                
                                {/* Role Description Guide */}
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                    <div className="text-xs font-bold text-blue-700 mb-2 uppercase">{t('userMgmt.roleGuide')}</div>
                                    <div className="space-y-2 text-xs text-blue-800">
                                        <div>
                                            <span className="font-bold">Admin:</span> {t('userMgmt.adminRoleDesc')}
                                        </div>
                                        <div>
                                            <span className="font-bold">Supervisor:</span> {t('userMgmt.supervisorRoleDesc')}
                                        </div>
                                        <div>
                                            <span className="font-bold">Staff:</span> {t('userMgmt.staffRoleDesc')}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-3">
                                    {projects.map((project) => {
                                        const access = formData.access.find(a => a.projectId === project.id);
                                        return (
                                            <div
                                                key={project.id}
                                                className="rounded-xl border border-slate-200 hover:border-[#10056E]/30 transition-colors"
                                            >
                                                <div className="flex items-center gap-3 p-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!access}
                                                        onChange={() => toggleProjectAccess(project.id)}
                                                        className="w-5 h-5 rounded border-slate-300 text-[#10056E] focus:ring-[#10056E]"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-slate-800">{project.name}</div>
                                                    </div>
                                                    {access && (
                                                        <select
                                                            value={access.role}
                                                            onChange={(e) => updateProjectRole(project.id, e.target.value)}
                                                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none text-sm font-medium"
                                                        >
                                                            {ROLES.filter(r => r !== 'Super Admin').map((role) => (
                                                                <option key={role} value={role}>{role}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                                {access && (
                                                    <div className="px-4 pb-4">
                                                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                            <div className="text-xs font-semibold text-slate-700 mb-1">
                                                                {ROLE_DESCRIPTIONS[access.role].access}
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                                {ROLE_DESCRIPTIONS[access.role].features.map((feature, idx) => (
                                                                    <span 
                                                                        key={idx}
                                                                        className="px-2 py-1 bg-[#10056E]/10 text-[#10056E] rounded text-xs font-medium"
                                                                    >
                                                                        {feature}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {projects.length === 0 && (
                                        <div className="text-center py-6 text-slate-400 text-sm">
                                            {t('userMgmt.noProjectsAvailable')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Hide Financials */}
                        {!formData.isSuperAdmin && (
                            <div className="flex items-start gap-4 p-4 rounded-xl border border-slate-200">
                                <div className="flex-1">
                                    <div className="font-semibold text-slate-800 mb-1">
                                        {t('userMgmt.hideFinancialInfo')}
                                    </div>
                                    <div className="text-sm text-slate-500">
                                        {t('userMgmt.userCannotViewPrices')}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, hideFinancials: !formData.hideFinancials })}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                        formData.hideFinancials ? 'bg-[#10056E]' : 'bg-slate-300'
                                    }`}
                                >
                                    <span
                                        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                            formData.hideFinancials ? 'translate-x-6' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>
                        )}

                        {/* Status */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                {t('userMgmt.accountStatus')} *
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all"
                            >
                                <option value="Aktif">{t('userMgmt.activeStatus')}</option>
                                <option value="Tidak Aktif">{t('userMgmt.inactiveStatus')}</option>
                            </select>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-8 pt-6 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-6 py-3 rounded-xl bg-[#10056E] text-white font-semibold hover:bg-[#0d0459] shadow-lg shadow-[#10056E]/30 transition-all"
                        >
                            {user ? t('userMgmt.updateUser') : t('userMgmt.createUser')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserManagementModal;
